import { Stack, type SxProps, type Theme } from "@mui/material";
import {
	type PropsWithChildren,
	type ReactElement,
	useLayoutEffect,
	useRef,
} from "react";
import { useLocation } from "react-router-dom";
import { useGoalsRightOptional } from "web_myplann/Api/Rights/RightsProvider";
import { MyPlannTopGlow } from "web_myplann/Shared/Components/MyPlannTopGlow";
import { useBottomBarSpacing } from "web_myplann/Shared/Hooks/useBottomBarSpacing";
import { MobileLayoutContent } from "web_myplann/Shared/Layouts/MobileLayoutContent";
import { MobileLayoutTopBar } from "web_myplann/Shared/Layouts/MobileLayoutTopBar";

type Props = {
	topBar?: ReactElement;
	bottomBar?: ReactElement;
	disableContentPadding?: boolean;
	fullWidth?: boolean;
	disableBottomBar?: boolean;
	mainSx?: SxProps<Theme>;
	disableBottomPadding?: boolean;
	disableTopPadding?: boolean;
	bgColor?: string;
	topBarBgColor?: string;
	overlayTopBar?: boolean;
	disableBottomBarSpacing?: boolean;
	topGlow?: boolean;
	scrollResetKey?: string | number | boolean;
};

export const MyPlannLayout = ({
	children,
	topBar,
	disableContentPadding,
	mainSx,
	disableBottomPadding,
	disableTopPadding,
	bgColor,
	topBarBgColor,
	overlayTopBar,
	disableBottomBarSpacing,
	topGlow,
	scrollResetKey,
}: PropsWithChildren<Props>) => {
	const bottomBarSpacing = useBottomBarSpacing();
	const hasGoalsRight = useGoalsRightOptional();
	const showGlow = !!topGlow && hasGoalsRight;
	const contentRef = useRef<HTMLDivElement>(null);
	const { pathname } = useLocation();

	useLayoutEffect(() => {
		contentRef.current?.scrollTo({ top: 0 });
	}, [pathname, scrollResetKey]);
	const baseSx: SxProps<Theme> = {
		...(bgColor ? { background: bgColor } : {}),
		...(!disableBottomBarSpacing && bottomBarSpacing
			? { paddingBottom: bottomBarSpacing }
			: {}),
	};
	const contentSx: SxProps<Theme> = [
		baseSx,
		...(showGlow ? [{ isolation: "isolate" as const }] : []),
		...(Array.isArray(mainSx) ? mainSx : mainSx ? [mainSx] : []),
	];

	return (
		<Stack
			height="100dvh"
			overflow="hidden"
			sx={{
				...(bgColor ? { background: bgColor } : {}),
			}}
		>
			<MobileLayoutTopBar
				topBar={topBar}
				disableTopPadding={disableTopPadding}
				overlayTopBar={overlayTopBar}
				topBarSx={{
					...((topBarBgColor ?? bgColor)
						? { backgroundColor: topBarBgColor ?? bgColor }
						: {}),
				}}
			/>

			<MobileLayoutContent
				ref={contentRef}
				disableContentPadding={disableContentPadding}
				disableBottomPadding={disableBottomPadding}
				mainSx={contentSx}
			>
				{showGlow && <MyPlannTopGlow />}
				{children}
			</MobileLayoutContent>
		</Stack>
	);
};
